/**
 * Device Fingerprint Utility
 *
 * Generates a STABLE per-browser device identifier for "trust this device"
 * MFA bypass.
 *
 * History: previously the fingerprint was computed from volatile inputs
 * (canvas hash, WebGL renderer, full UA, languages, pixelRatio) and cached
 * only in sessionStorage. That cache was wiped whenever the tab/PWA closed,
 * and the recomputed hash drifted after browser/OS updates — so trusted
 * devices kept being asked for MFA codes.
 *
 * Now: we generate a random UUID once per browser profile and store it in
 * localStorage under `hibane_admin_device_id`. The fingerprint returned to
 * the server is a 32-char hash of that UUID. It survives:
 *   - closing the tab/browser
 *   - force-quitting the installed PWA
 *   - browser and OS updates
 *   - admin sign-outs and silent session clears
 *
 * Backwards compat: on the first call after this ships, if no stable UUID
 * exists yet AND we can compute the legacy fingerprint, we return the legacy
 * value so an already-trusted device still matches its `admin_trusted_devices`
 * row. We then write the new stable UUID so subsequent calls use the stable
 * path. The trust row self-heals on the next login (when the user re-ticks
 * "trust this device" or the post-login trust upsert runs).
 */
 
const STABLE_DEVICE_ID_KEY = "hibane_admin_device_id";
const LEGACY_FINGERPRINT_KEY = "admin_device_fingerprint";

// Simple hash function for strings
async function hashString(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

function safeLocal(): Storage | null {
  try { return window.localStorage; } catch { return null; }
}
function safeSession(): Storage | null {
  try { return window.sessionStorage; } catch { return null; }
}

function readStableDeviceId(): string | null {
  return safeLocal()?.getItem(STABLE_DEVICE_ID_KEY) ?? null;
}

function writeStableDeviceId(id: string): void {
  try { safeLocal()?.setItem(STABLE_DEVICE_ID_KEY, id); } catch { /* noop */ }
}

function generateUuid(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* noop */ }
  // Fallback (very old browsers): RFC4122-ish v4
  const bytes = new Uint8Array(16);
  try { crypto.getRandomValues(bytes); } catch {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
}

// --- Legacy fingerprint (kept for backwards compatibility on first call) ---

function collectDeviceData(): Record<string, string> {
  const data: Record<string, string> = {};
  data.screenWidth = String(window.screen.width);
  data.screenHeight = String(window.screen.height);
  data.colorDepth = String(window.screen.colorDepth);
  data.pixelRatio = String(window.devicePixelRatio || 1);
  data.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  data.timezoneOffset = String(new Date().getTimezoneOffset());
  data.language = navigator.language;
  data.languages = navigator.languages?.join(',') || navigator.language;
  data.platform = navigator.platform;
  data.userAgent = navigator.userAgent;
  data.hardwareConcurrency = String(navigator.hardwareConcurrency || 'unknown');
  data.maxTouchPoints = String(navigator.maxTouchPoints || 0);
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
      data.canvasHash = canvas.toDataURL().substring(0, 50);
    }
  } catch {
    data.canvasHash = 'unavailable';
  }
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        data.webglVendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'unknown';
        data.webglRenderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown';
      }
    }
  } catch {
    data.webglVendor = 'unavailable';
    data.webglRenderer = 'unavailable';
  }
  return data;
}

async function computeLegacyFingerprint(): Promise<string> {
  try {
    const data = collectDeviceData();
    const dataString = Object.entries(data)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}:${v}`)
      .join('|');
    const fp = await hashString(dataString);
    return fp.substring(0, 32);
  } catch {
    const fp = await hashString(navigator.userAgent || 'unknown');
    return fp.substring(0, 32);
  }
}

// Exposed for callers that explicitly want the legacy fingerprint (none today).
export async function generateDeviceFingerprint(): Promise<string> {
  return computeLegacyFingerprint();
}

/**
 * Return a stable, persistent device fingerprint.
 *
 * - If a stable UUID is already saved → return its hash (the normal path).
 * - If not, and we have a cached legacy fingerprint in sessionStorage from an
 *   earlier visit → return that legacy value (so we still match the existing
 *   trusted-device row) and provision a stable UUID for next time.
 * - If neither exists yet → return the legacy fingerprint and provision a
 *   stable UUID. The legacy fingerprint is what already-trusted devices have
 *   on file; returning it lets MFA-skip work on this login. After this login,
 *   the next "trust this device" upsert (or any login on this device) will
 *   replace the trust row with the stable UUID hash.
 */
export async function getDeviceFingerprint(): Promise<string> {
  // 1) Stable path
  const stableId = readStableDeviceId();
  if (stableId) {
    const fp = await hashString(`hibane-stable-device:${stableId}`);
    return fp.substring(0, 32);
  }

  // 2) Legacy-compat path — ONLY when a legacy fingerprint already exists in
  //    sessionStorage from an earlier visit in THIS browser session. That
  //    cached value means the user may have an `admin_trusted_devices` row
  //    keyed on the legacy hash from before this code shipped, so we keep
  //    returning it (once) so the trusted-device skip still works. Provision
  //    a stable UUID at the same time; the trust row self-heals on next login.
  const cachedLegacy = safeSession()?.getItem(LEGACY_FINGERPRINT_KEY) ?? null;
  if (cachedLegacy) {
    const newId = generateUuid();
    writeStableDeviceId(newId);
    return cachedLegacy;
  }

  // 3) Fresh browser, no legacy cache → provision a stable UUID and return
  //    ITS hash immediately. This guarantees that admin-mfa-challenge and
  //    admin-mfa-verify (and every subsequent call) see the same fingerprint
  //    within a single login flow. Previously this returned the volatile
  //    legacy fingerprint while side-effect-writing a new stable UUID, which
  //    caused a fingerprint mismatch between challenge and verify and made
  //    MFA codes look invalid on first login from a new device.
  const newId = generateUuid();
  writeStableDeviceId(newId);
  const fp = await hashString(`hibane-stable-device:${newId}`);
  return fp.substring(0, 32);
}


/**
 * Clear ONLY the volatile legacy fingerprint cache. The stable device ID is
 * intentionally preserved across logouts, supersessions, and "clear admin
 * session" calls — that is the whole point: "this is the same browser" should
 * survive any client-side wipe short of the user clearing their browser data.
 */
export function clearDeviceFingerprint(): void {
  try { safeSession()?.removeItem(LEGACY_FINGERPRINT_KEY); } catch { /* noop */ }
}
